/**
 * Tables and Files.
 *
 * One half of `AGENT_COPY`. The shape and the rules are documented on the
 * barrel in `../agent-copy.ts`; this file only holds entries.
 *
 * One Chinese word per thing across both surfaces, because a row is a 行 on the
 * grid, in the row modal and in the import summary: 文件 file, 文件夹 folder,
 * 数据表 table, 行 row, 列 column, 单元格 cell, 视图 view, 智能填充 enrichment.
 */
export const DATA: Record<string, string> = {
	// -- the files list
	Files: "文件",
	File: "文件",
	Audio: "音频",
	Video: "视频",
	Image: "图片",
	Page: "页面",
	Download: "下载",
	Share: "分享",
	Preview: "预览",
	Split: "分栏",
	"New file": "新建文件",
	"Copy Link": "复制链接",
	"Copied!": "已复制！",
	"Search files...": "搜索文件...",
	"Find in files": "在文件中查找",
	"Drop to upload": "拖放以上传",
	"Release files here to add them to this workspace": "在此松开文件，即可添加到本工作区",

	// -- the files list's filters
	"File Type": "文件类型",
	Images: "图片",
	Small: "小",
	Large: "大",
	"Small (< 1 MB)": "小（< 1 MB）",
	"Medium (1–10 MB)": "中（1–10 MB）",
	"Large (> 10 MB)": "大（> 10 MB）",
	"Uploaded By": "上传者",
	"Clear all filters": "清除全部筛选",
	"Type: {value}": "类型：{value}",
	"Type: {count} selected": "类型：已选 {count} 项",
	"Size: {value}": "大小：{value}",
	"Size: {count} selected": "大小：已选 {count} 项",
	"Uploaded by: {value}": "上传者：{value}",
	"Uploaded by: {count} members": "上传者：{count} 位成员",

	// -- uploading, saving and unzipping
	"{name} exceeds the 5 GiB upload limit": "{name} 超过 5 GiB 上传上限",
	"{count} files exceed the 5 GiB upload limit": "{count} 个文件超过 5 GiB 上传上限",
	'Failed to upload "{name}"': "上传“{name}”失败",
	'Failed to download "{name}"': "下载“{name}”失败",
	"Failed to download the selected files": "下载所选文件失败",
	'Failed to save "{name}"': "保存“{name}”失败",
	"Failed to save the file": "保存文件失败",
	"Copied link to clipboard": "链接已复制到剪贴板",
	"Failed to copy link": "复制链接失败",
	"Unsaved Changes": "未保存的更改",
	"You have unsaved changes. Are you sure you want to discard them?": "你有未保存的更改，确定要放弃吗？",
	"Keep editing": "继续编辑",
	"Discard Changes": "放弃更改",
	"Unzip archive?": "解压压缩包？",
	"This will unzip ": "这会把 ",
	"this archive": "该压缩包",
	" into a new folder beside it.": " 解压到同级的新文件夹中。",
	Unzip: "解压",
	"Unzipping...": "解压中...",
	"Failed to load files": "加载文件失败",
	"Something went wrong while loading your files. Please try again.": "加载文件时出错，请重试。",

	// -- the file row and list context menus
	Open: "打开",
	"Open the run that made it": "查看生成它的运行",
	"Delete Items": "删除项目",
	"Delete File": "删除文件",
	"1 item": "1 个项目",
	"{count} items": "{count} 个项目",
	"? {consequence}": "吗？{consequence}",
	"This will also delete files and folders inside any selected folders.": "所选文件夹内的文件和子文件夹也会一并删除。",
	"This will also delete files and folders inside it.": "其中的文件和子文件夹也会一并删除。",
	"You can restore them from Recently Deleted in Settings.": "可以在设置的“最近删除”中恢复。",
	"You can restore it from Recently Deleted in Settings.": "可以在设置的“最近删除”中恢复。",

	// -- sharing a file
	"Share file": "分享文件",
	"Share {name}": "分享 {name}",
	Access: "访问方式",
	"File access": "文件访问方式",
	Public: "公开",
	Password: "密码",
	Email: "邮箱",
	"Sharing...": "分享中...",
	"Updating...": "更新中...",
	Unshare: "取消分享",
	"Unsharing...": "取消分享中...",
	"File shared": "文件已分享",
	"Sharing updated": "分享设置已更新",
	"File unshared": "已取消分享",
	"Copy link": "复制链接",
	"Unable to load the current sharing settings. Close and try again.": "无法加载当前分享设置，请关闭后重试。",
	"This sharing method is disabled by an administrator.": "管理员已停用该分享方式。",
	"Public sharing is disabled for this workspace by an administrator.": "管理员已停用本工作区的公开分享。",
	"Anyone with the link and the password can view and download this file.":
		"任何人只要拿到链接和密码，就能查看并下载该文件。",
	"Only allowed emails can access this file after a one-time code.": "仅允许的邮箱在通过一次性验证码后可访问该文件。",
	"Only allowed emails signed in via SSO can access this file.": "仅允许的邮箱通过 SSO 登录后可访问该文件。",
	"Anyone with the link can view and download this file.": "任何人只要拿到链接，就能查看并下载该文件。",
	"Update to make this file accessible to anyone with the link.": "点击“更新”，让任何拿到链接的人都能访问该文件。",
	"Share to make this file accessible to anyone with the link.": "点击“分享”，让任何拿到链接的人都能访问该文件。",
	"Leave blank to keep the current password.": "留空则沿用当前密码。",
	"Anyone with the link must enter this password.": "拿到链接的人需要输入该密码。",
	"Enter a password": "输入密码",
	"Allowed emails": "允许的邮箱",
	"Enter emails or domains": "输入邮箱或域名",
	"Add email or domain": "添加邮箱或域名",
	"Unshare file?": "取消分享该文件？",
	"Are you sure you want to unshare this file? Anyone with the link will lose access.":
		"确定要取消分享该文件吗？拿到链接的人将无法再访问。",

	// -- the file preview frames
	"Preview not available for .{ext} files": "暂不支持预览 .{ext} 文件",
	"Preview not available for this file": "暂不支持预览该文件",
	"Use the download button to view this file": "请使用下载按钮查看该文件",
	"Failed to preview {label}": "无法预览{label}",
	"An unexpected error occurred": "发生了意外错误",
	"Couldn't generate this document preview. Re-run the file generation to rebuild it.":
		"无法生成该文档的预览，请重新运行文件生成以重建。",
	"this file": "该文件",
	document: "文档",
	spreadsheet: "电子表格",
	presentation: "演示文稿",
	audio: "音频",
	video: "视频",
	CSV: "CSV",
	PDF: "PDF",
	page: "页",
	slide: "张幻灯片",
	"No data to display": "没有可显示的数据",
	"Failed to parse spreadsheet": "解析电子表格失败",
	"Failed to parse sheet": "解析工作表失败",
	"Failed to render document": "渲染文档失败",
	"Failed to render presentation": "渲染演示文稿失败",
	"Failed to render diagram": "渲染图表失败",
	"Failed to preview PDF": "无法预览 PDF",
	"Failed to load PDF": "加载 PDF 失败",
	"Failed to load file content": "加载文件内容失败",
	"{filename} page {page}": "{filename} 第 {page} 页",
	"Showing first {rows} rows and {columns} columns.": "仅显示前 {rows} 行、前 {columns} 列。",
	"Showing first {rows} rows.": "仅显示前 {rows} 行。",
	"Showing first {columns} columns.": "仅显示前 {columns} 列。",
	"Download the file to view all data.": "下载文件可查看全部数据。",
	"failed to load the chart renderer": "图表渲染器加载失败",
	"invalid ECharts option": "ECharts 配置无效",
	"failed to read the table": "读取数据表失败",
	"Rendering…": "渲染中…",
	"Invalid diagram": "图表定义无效",
	"HTML Preview": "HTML 预览",
	"SVG preview": "SVG 预览",
	"Zoomable preview": "可缩放预览",
	"Previous {label}": "上一{label}",
	"Next {label}": "下一{label}",
	"Reset zoom": "重置缩放",
	Reset: "重置",
	"Zoom out": "缩小",
	"Zoom in": "放大",

	// -- importing a capped CSV as a table
	'Importing "{name}" as a table': "正在把“{name}”导入为数据表",
	"This runs in the background.": "该任务在后台运行。",
	"View tables": "查看数据表",
	"Showing the first {count} rows": "仅显示前 {count} 行",
	"Import this file as a table to view all of its rows.": "把该文件导入为数据表即可查看全部行。",
	"Import as a table": "导入为数据表",

	// -- editing a file's text
	"Saving paused: the file changed elsewhere. Your local draft is preserved. Reload replaces it with the latest version.":
		"已暂停保存：该文件在别处被改动。你的本地草稿仍在，重新加载会用最新版本替换它。",
	"Download local draft": "下载本地草稿",
	"Could not reload the file. Your local draft is unchanged.": "无法重新加载该文件，本地草稿未受影响。",
	"Reloading…": "重新加载中…",
	"Discard draft and reload": "放弃草稿并重新加载",
	"Resize split": "调整分栏宽度",
	"Paste would make this file too large to edit": "粘贴后文件将过大，无法继续编辑",
	"Inline editing supports files up to {limit}. Import or replace the file to keep larger content available without loading it into the editor.":
		"内联编辑最多支持 {limit} 的文件。更大的内容请导入或替换文件，以免整份载入编辑器。",
	"Add to Chat": "添加到对话",
	"Copy all": "全部复制",
	Find: "查找",
	"Find in document": "在文档中查找",

	// -- the rich markdown editor
	"Plain text": "纯文本",
	"Show diagram": "显示图表",
	"Show source": "显示源码",
	"Code language": "代码语言",
	"Toggle line wrap": "切换自动换行",
	"Copy code": "复制代码",
	"Raw HTML": "原始 HTML",
	Footnote: "脚注",
	Raw: "原始内容",
	"Resize image": "调整图片大小",
	"Link URL": "链接地址",
	"Paste or type a link…": "粘贴或输入链接…",
	"Link editing": "编辑链接",
	"Text formatting": "文本格式",
	"Remove link": "移除链接",
	"Apply link": "应用链接",
	"Edit link": "编辑链接",
	Copied: "已复制",
	Link: "链接",
	Bold: "加粗",
	Italic: "斜体",
	Strikethrough: "删除线",
	Highlight: "高亮",
	Code: "代码",
	"Heading 1": "一级标题",
	"Heading 2": "二级标题",
	"Heading 3": "三级标题",
	"Bulleted list": "无序列表",
	"Numbered list": "有序列表",
	Checklist: "任务列表",
	Quote: "引用",
	"Code block": "代码块",
	Table: "表格",
	Divider: "分隔线",
	"Table editing": "编辑表格",
	"Insert row above": "在上方插入行",
	"Insert row below": "在下方插入行",
	"Delete row": "删除行",
	"Insert column left": "在左侧插入列",
	"Insert column right": "在右侧插入列",
	"Delete column": "删除列",
	"Delete table": "删除表格",
	Mentions: "提及",
	Commands: "命令",
	"No results": "无匹配结果",
	Basic: "基础",
	Lists: "列表",
	Blocks: "区块",
	Media: "媒体",
	Folders: "文件夹",
	Tables: "数据表",
	Skills: "技能",
	"Pasted text kept without automatic formatting": "已保留粘贴内容，但未自动套用格式",
	"Adding that formatting would exceed the rich-text editing limit.": "套用这些格式会超出富文本编辑上限。",
	"Paste is too large for rich-text editing": "粘贴内容过大，无法进行富文本编辑",
	"Rich-text editing supports up to {count} characters. Use the source editor for larger documents.":
		"富文本编辑最多支持 {count} 个字符，更长的文档请使用源码编辑器。",
	"Keep this document under {limit}, or import the content as a file.":
		"请将该文档控制在 {limit} 以内，或把内容作为文件导入。",
	"Replacement is too large": "替换内容过大",
	"Use the source editor for changes that exceed the rich-text editing limit.":
		"超出富文本编辑上限的修改，请使用源码编辑器。",
	'Uploading "{name}"…': "正在上传“{name}”…",
	"The image was uploaded to the workspace but was not inserted.": "图片已上传到工作区，但未插入文档。",
	"The insertion location changed. Choose a new location and select the image again.":
		"插入位置已变化，请重新选择位置后再次选取图片。",
	"This document needs source editing to preserve its content or handle its size.":
		"该文档需要用源码编辑，才能保住其内容或应付其体量。",
	"Edit source": "编辑源码",

	// -- the tables list
	Columns: "列",
	Rows: "行",
	"Search tables...": "搜索数据表...",
	"Row Count": "行数",
	"Small (1–100)": "小（1–100）",
	"Large (101+)": "大（101+）",
	"Small (1–100 rows)": "小（1–100 行）",
	"Large (101+ rows)": "大（101+ 行）",
	"Rows: {value}": "行数：{value}",
	"Rows: {count} selected": "行数：已选 {count} 项",
	"Failed to load tables": "加载数据表失败",
	"Something went wrong while loading the tables. Please try again.": "加载数据表时出错，请重试。",
	"Failed to load table": "加载数据表失败",
	"Something went wrong while loading this table. The table may have been deleted or you may not have permission to view it.":
		"加载该数据表时出错。它可能已被删除，或者你没有查看权限。",
	"Go back": "返回",
	"Select {count} or fewer items to move at once": "一次最多移动 {count} 个项目",
	"Select {count} or fewer items to delete at once": "一次最多删除 {count} 个项目",
	"Delete Table": "删除数据表",
	"this table": "该数据表",
	"All {count} rows will be removed.": "其中的 {count} 行将全部删除。",
	"All of their rows will be removed.": "它们的全部行都会被删除。",
	"Every table and subfolder inside it will be deleted too.": "其中的数据表和子文件夹也会一并删除。",
	"Every table and subfolder inside the selected folders will be deleted too.":
		"所选文件夹内的数据表和子文件夹也会一并删除。",
	" You can restore those tables from Recently Deleted in Settings.": " 可以在设置的“最近删除”中恢复这些数据表。",
	"View Schema": "查看结构",
	"Failed to export table": "导出数据表失败",
	"Failed to import CSV": "导入 CSV 失败",
	"No CSV or TSV files selected": "未选择 CSV 或 TSV 文件",

	// -- the CSV import dialog
	'Imported {count} rows into "{table}"': "已向“{table}”导入 {count} 行",
	"At least {count} malformed rows were skipped.": "至少跳过了 {count} 行格式错误的记录。",
	"{inserted} of {total} rows": "{inserted} / {total} 行",
	"Imported {inserted} of {total} rows. {message}": "已导入 {total} 行中的 {inserted} 行。{message}",
	"This file has {count} rows. A table holds at most {max}.": "该文件有 {count} 行，一个数据表最多容纳 {max} 行。",
	"The table has {existing} rows and the file has {count}. A table holds at most {max}.":
		"该数据表已有 {existing} 行，文件有 {count} 行，一个数据表最多容纳 {max} 行。",
	"Import CSV into {name}": "导入 CSV 到 {name}",
	"Map columns to append or replace rows in this table": "映射各列，以向该数据表追加或替换行",
	"Drop CSV or TSV here or click to browse": "将 CSV 或 TSV 拖到此处，或点击选择文件",
	"Parsing...": "解析中...",
	"Only CSV and TSV files are supported": "仅支持 CSV 和 TSV 文件",
	"Failed to parse CSV": "解析 CSV 失败",
	"Change file": "更换文件",
	"{count} columns": "{count} 列",
	Mode: "模式",
	Append: "追加",
	"Replace all rows": "替换全部行",
	"Column mapping": "列映射",
	"Create columns for {count} unmapped": "为 {count} 个未映射列新建列",
	"CSV column": "CSV 列",
	"Target column": "目标列",
	"Do not import": "不导入",
	"+ Create new column": "+ 新建列",
	"{name} (required)": "{name}（必填）",
	"{mapped} mapped · {created} new columns · {skipped} skipped":
		"已映射 {mapped} 列 · 新建 {created} 列 · 跳过 {skipped} 列",
	"{mapped} mapped · {skipped} skipped": "已映射 {mapped} 列 · 跳过 {skipped} 列",
	"Missing required column(s): {columns}": "缺少必填列：{columns}",
	"Multiple CSV columns target: {columns} (pick one)": "多个 CSV 列指向：{columns}（只能选一个）",
	"Replace will permanently delete the {count} existing rows before inserting the new rows.":
		"“替换”会先永久删除现有的 {count} 行，再写入新行。",
	"Replace rows": "替换行",
	"Append rows": "追加行",
	"Replacing...": "替换中...",
	'{count} rows conflict on unique column "{column}" ({examples})': "{count} 行在唯一列“{column}”上冲突（{examples}）",
	", +{count} more": "，另有 {count} 个",

	// -- the table's header, views and locks
	Views: "视图",
	"Lock settings": "锁定设置",
	"Table locks": "数据表锁",
	"Lock {noun}": "锁定{noun}",
	"adding rows": "新增行",
	"editing rows": "编辑行",
	"deleting rows": "删除行",
	"changing columns": "修改列",
	"On: no new rows can be added — by anyone, including CSV import, the API, workflow blocks, and Ondit.":
		"开启后：任何人都无法新增行，包括 CSV 导入、API、工作流节点和 Ondit。",
	"On: existing cell values cannot be changed. Workflow and enrichment columns still populate.":
		"开启后：已有单元格的值无法修改；工作流列和智能填充列仍会写入。",
	"On: rows cannot be deleted, and the table cannot be archived.": "开启后：无法删除行，数据表也无法归档。",
	"On: columns cannot be added, renamed, retyped, or removed.": "开启后：无法新增、重命名、改类型或删除列。",
	Unlocked: "未锁定",
	"anyone with edit access can change this table.": "拥有编辑权限的人都可以修改该数据表。",
	"Read-only": "只读",
	"no one can change this table’s rows or columns.": "任何人都无法修改该数据表的行或列。",
	"Append-only": "仅可追加",
	"rows can be added, but not edited or deleted, and columns are locked.": "可以新增行，但不能编辑或删除，且列已锁定。",
	"rows can be added, but not edited or deleted.": "可以新增行，但不能编辑或删除。",
	"{list} locked.": "{list}已锁定。",
	"Adding rows is locked": "新增行已锁定",
	"No new rows can be added until an admin unlocks this table.": "在管理员解锁该数据表之前，无法新增行。",
	"This table is append-only": "该数据表仅可追加",
	"Rows can’t be edited once added, so typing one into the grid is unavailable. Import a CSV, or add rows from the API, a workflow, or Ondit.":
		"行一旦写入就不能再编辑，因此无法在表格中直接输入。请导入 CSV，或通过 API、工作流或 Ondit 新增行。",
	"Changing columns is locked": "修改列已锁定",
	"Columns can’t be added, renamed, retyped, or removed until an admin unlocks this table.":
		"在管理员解锁该数据表之前，无法新增、重命名、改类型或删除列。",
	"Deleting columns is locked": "删除列已锁定",
	"Removing a column deletes its value from every row, so it’s blocked while deleting is locked.":
		"删除列会清空每一行的对应值，因此在删除锁定期间不可执行。",
	"Editing rows is locked": "编辑行已锁定",
	"Existing cell values can’t be changed until an admin unlocks this table.":
		"在管理员解锁该数据表之前，无法修改已有单元格的值。",
	"An admin has locked {list} on this table.": "管理员已锁定该数据表的{list}。",
	"Nothing is locked on this table.": "该数据表没有任何锁定。",

	// -- the grid
	"New row": "新建行",
	"Table not found": "找不到该数据表",
	"This table may have been deleted or moved": "该数据表可能已被删除或移动",
	"Find in table": "在数据表中查找",
	running: "运行中",
	"Column options": "列选项",
	"Edit cell": "编辑单元格",
	"Filter by cell value": "按单元格的值筛选",
	"Duplicate row": "复制本行",
	"Delete {count} rows": "删除 {count} 行",
	"Add row to Chat": "把本行加入对话",
	"Add {count} rows to Chat": "把 {count} 行加入对话",
	"Add cell range to Chat": "把所选单元格加入对话",
	"Failed to load all rows. Try again.": "加载全部行失败，请重试。",
	"Failed to clear cells — please try again": "清空单元格失败，请重试",
	"Failed to clear column values — please try again": "清空该列的值失败，请重试",
	"Clipboard access is unavailable in this context": "当前环境无法访问剪贴板",
	"Copying {count} rows…": "正在复制 {count} 行…",
	"Cutting {count} rows…": "正在剪切 {count} 行…",
	"Copied {count} rows": "已复制 {count} 行",
	"Cut {count} rows": "已剪切 {count} 行",
	"Copied first {count} rows — export to CSV for the rest": "已复制前 {count} 行，其余请导出 CSV",
	"Cut first {count} rows — export to CSV for the rest": "已剪切前 {count} 行，其余请导出 CSV",
	"Failed to copy — please try again": "复制失败，请重试",
	"Failed to cut — please try again": "剪切失败，请重试",
	"Copied to clipboard, but clearing the cells failed — please try again": "已复制到剪贴板，但清空单元格失败，请重试",
	"Paste is too large for the table editor": "粘贴内容过大，表格编辑器无法处理",
	"Paste up to {limit} at once, or use CSV import for larger datasets.":
		"单次粘贴最多 {limit}，更大的数据请用 CSV 导入。",
	"Paste has too many rows": "粘贴的行数过多",
	"Paste up to {count} rows at once, or use CSV import for larger datasets.":
		"单次粘贴最多 {count} 行，更大的数据请用 CSV 导入。",

	// -- cell states
	Waiting: "等待中",
	"Not found": "未找到",
	"(empty)": "（空）",
	save: "保存",
	cancel: "取消",
	"Loading timezone…": "正在加载时区…",
	"Invalid timezone": "时区无效",
	"Timezone unavailable": "时区不可用",
	"mm/dd/yyyy": "mm/dd/yyyy",

	// -- the row modal
	"Edit Row": "编辑行",
	"Update values for {name}": "修改 {name} 的字段值",
	"Update Row": "保存修改",
	"Delete Row": "删除行",
	"Delete {count} Rows": "删除 {count} 行",
	"Are you sure you want to delete this row? ": "确定要删除这一行吗？",
	"Are you sure you want to delete these {count} rows? ": "确定要删除这 {count} 行吗？",
	"This will permanently remove all data in this row.": "该行的全部数据都会被永久删除。",
	"This will permanently remove all data in these rows.": "这些行的全部数据都会被永久删除。",
	"Failed to edit row": "编辑行失败",
	"Failed to delete rows": "删除行失败",
	"(unique)": "（唯一）",
	"Type: {type}": "类型：{type}",
	"Type: {type} (optional)": "类型：{type}（可选）",
	"currency ({code})": "货币（{code}）",
	"No value": "无值",
	"Add time": "添加时间",
	"Enter {name}": "输入{name}",

	// -- the column configuration sidebar
	"Configure column": "配置列",
	"Column name": "列名",
	"Column name is required": "列名不能为空",
	Currency: "货币",
	"Select currency": "选择货币",
	"Search currencies": "搜索货币",
	Options: "选项",
	"Option name": "选项名称",
	"Add option": "添加选项",
	option: "选项",
	"Add at least one option": "至少添加一个选项",
	"Option names cannot be empty": "选项名称不能为空",
	"Option names must be unique": "选项名称不能重复",
	Multiselect: "多选",
	Unique: "唯一",
	"Saving…": "保存中…",
	'Added "{name}"': "已添加“{name}”",
	'Saved "{name}"': "已保存“{name}”",
	"Only one {type} column allowed per table": "每个数据表只能有一个{type}列",
	"Only {count} {type} columns allowed per table": "每个数据表最多只能有 {count} 个{type}列",
	Select: "选项",
	Expiration: "过期时间",
	"Select options": "选择选项",
	"Select an option": "选择一个选项",

	// -- the column header menu
	"Edit column": "编辑列",
	"Pin column": "固定列",
	"Unpin column": "取消固定列",
	"Delete Column": "删除列",
	"Delete {count} Columns": "删除 {count} 列",
	"this column": "该列",
	"This will remove all data in this column.": "该列的全部数据都会被删除。",
	"This will remove all data in these columns.": "这些列的全部数据都会被删除。",
	" You can undo this action.": " 此操作可以撤销。",
	"Sort ascending": "升序排序",
	"Sort descending": "降序排序",
	"Clear sort": "清除排序",

	// -- the filter bar
	"Apply filter": "应用筛选",
	"Remove filter": "移除该筛选",
	Where: "当",
	or: "或",
	Column: "列",
	"Select a value": "选择一个值",
	"Enter a value": "输入一个值",
	equals: "等于",
	"not equals": "不等于",
	contains: "包含",
	"does not contain": "不包含",
	"starts with": "开头是",
	"ends with": "结尾是",
	"greater than": "大于",
	"greater or equal": "大于等于",
	"less than": "小于",
	"less or equal": "小于等于",
	"in array": "在数组中",
	"not in array": "不在数组中",
	"is empty": "为空",
	"is not empty": "不为空",

	// -- workflow and enrichment columns
	"Select a workflow": "选择一个工作流",
	Required: "必填",
	Enrichment: "智能填充",
	Inputs: "输入",

	// -- the enrichment catalogue
	"Company Domain": "公司域名",
	"Find a company's website domain from its name.": "根据公司名称查找其网站域名。",
	"Company Info": "公司信息",
	"Look up a company's size and description from its domain.": "根据公司域名查询其规模和简介。",
	"Email Verification": "邮箱验证",
	"Check an email address's deliverability and risk status.": "检查邮箱地址的可送达性和风险状态。",
	"Phone Number": "电话号码",
	"Find a contact's phone number from their name, company, or LinkedIn URL.":
		"根据姓名、公司或 LinkedIn 链接查找联系人的电话号码。",
	"LinkedIn Profile": "LinkedIn 主页",
	"Find a person's LinkedIn profile URL from their name, email, and company.":
		"根据姓名、邮箱和公司查找此人的 LinkedIn 主页链接。",
	"Work Email": "工作邮箱",
	"Find a person's work email from their name, company, or LinkedIn URL.":
		"根据姓名、公司或 LinkedIn 链接查找此人的工作邮箱。",
	"Company name": "公司名称",
	"Company domain": "公司域名",
	"Full name": "姓名",
	"LinkedIn URL": "LinkedIn 链接",

	// -- the enrichment run details
	Result: "结果",
	"Total cost": "总花费",
	"Not run": "未运行",
};
